// tslint:disable: max-line-length
import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Select, Store } from '@ngxs/store';
import { User } from 'app/classes/user';
import { ChartDialogComponent } from 'app/components/chart-dialog/chart-dialog.component';
import {
  AddFromTemplateDialogComponent,
  AddFromTemplateDialogComponentData,
} from 'app/core/components/add-from-template-dialog/add-from-template-dialog.component';
import {
  AddLibraryDialogComponent,
  AddLibraryDialogComponentData,
} from 'app/core/components/add-library-dialog/add-library-dialog.component';
import { CopyPlanDialogComponent, CopyPlanDialogComponentData } from 'app/core/components/copy-plan-dialog/copy-plan-dialog.component';
import { SelectGroupsDialogComponent } from 'app/core/components/select-groups-dialog/select-groups-dialog.component';
import {
  SelectWeekDialogComponent,
  SelectWeekDialogComponentData,
  SelectWeekDialogComponentResult,
} from 'app/core/components/select-week-dialog/select-week-dialog.component';
import {
  UserSearchComponent,
  UserSearchComponentData,
  UserSearchComponentResult,
} from 'app/core/components/user-search/user-search.component';
import { Day } from 'app/document/components/document-planning/document-calendar/document-calendar.component';
// import { DocumentPrintJob, DocumentPrintService } from 'app/document/services/document-print.service';
import { DocumentService, SyncStatus } from 'app/document/services/document.service';
import { EventBusService } from 'app/document/services/event-bus.service';
import { PlaycardDownloadService } from 'app/document/services/playcard-download.service';
import * as GuidedTourActions from 'app/guided-tour/state/guided-tour.actions';
import { GuidedTourSelectors } from 'app/guided-tour/state/guided-tour.selectors';
import { AuthService } from 'app/services/auth.service';
import { BackgroudSyncService } from 'app/services/backgroud-sync.service';
import { GuidedTourDataService } from 'app/services/guided-tour-data.service';
import { OfflineService } from 'app/services/offline.service';

import {
  DocumentModelFactoryService,
  DocumentModelType,
  Exercise,
  Phase,
  PhaseType,
  PlanDocument,
  PlanDocumentUtils as Utils,
  PlannedTier,
  PlannedTierRow,
  RestrictLabel,
  Schedule,
  Template,
  UserLibrary,
  Workout,
  WorkoutType,
} from 'fitforce-document-sync';
import { ObserverService } from 'fitforce-offline-first';
import { cloneDeep, has, isArray, update } from 'lodash';
import * as moment from 'moment';
import { Observable, of, Subscription } from 'rxjs';
import { concatMap, mergeMap, tap } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { AddRestrictionDialogComponent } from './document-calendar/add-restriction-dialog/add-restriction-dialog.component';

// tslint:enable: max-line-length

export const copyDayTopic = 'copyDay';
export const dayViewTopic = 'dayView';
export const workoutUpdatedTopic = 'workoutUpdated';
export interface CopyDayState {
  copying: boolean;
  pasted: boolean;
  cancelled: boolean;
  workoutId: string;
}

interface SelectedDay extends Day {
  date: moment.Moment;
  workout: Workout;
  workouts: Workout[];
  workoutType?: WorkoutType;
  restrictLabel?: RestrictLabel;
  phase: Phase;
}

export interface ExerciseCategory {
  id: string;
  name: string;
}

@Component({
  selector: 'document-planning',
  templateUrl: './document-planning.component.html',
  styleUrls: ['./document-planning.component.scss'],
})
export class DocumentPlanningComponent implements OnInit, AfterViewInit, OnDestroy {
  private subscriptions: Subscription[] = [];

  isLoading: boolean;
  currentUser: User;
  lastUpdated: number;
  monthView: boolean;
  dayView: boolean;
  isSynced: boolean;
  guidedTourToggledOn = false;
  guidedTourStatusSub: Subscription;

  @Select(GuidedTourSelectors.toggleStatus) guidedTourToggleStatus$: Observable<boolean>;

  selectedDay: SelectedDay;
  copying: boolean;

  get workoutTypes(): WorkoutType[] {
    return this.documentService.store.exerciseProgram.document.workout_types;
  }

  get restrictLabels(): RestrictLabel[] {
    return this.documentService.store.exerciseProgram.document.restrict_labels;
  }

  get phaseTypes(): PhaseType[] {
    return this.documentService.store.exerciseProgram.document.phases;
  }

  get syncing(): boolean {
    return this.backgroundSyncService.syncing;
  }

  get plan(): PlanDocument {
    return this.documentService.store.currentPlanDocument &&
      this.route.snapshot.params.id === this.documentService.store.currentPlanDocument.uuid
      ? this.documentService.store.currentPlanDocument
      : null;
  }

  get isOwner(): boolean {
    return (
      this.currentUser &&
      this.documentService.store.currentPlanDocument &&
      this.documentService.store.currentPlanDocument.userUUID === this.currentUser.uuid
    );
  }

  get sharedWithUsers(): string[] {
    const foundPlanMetadata = this.documentService.store.planMetadata.find(plan => plan.uuid === this.plan.uuid);
    return foundPlanMetadata ? foundPlanMetadata.sharedWithUsers.map(u => u.uuid) : [];
  }

  get publishedGroups(): string[] {
    const foundPlanMetadata = this.documentService.store.planMetadata.find(plan => plan.uuid === this.plan.uuid);
    return foundPlanMetadata ? foundPlanMetadata.publishedToGroups.map(u => u.uuid) : [];
  }

  get isOnline(): boolean {
    return this.offline.isOnline();
  }

  get exercises(): Exercise[] {
    return this.documentService.store.exerciseProgram.document.exercises;
  }

  get userLibrary(): UserLibrary {
    return this.documentService.store.userLibrary;
  }

  get exerciseCategories(): ExerciseCategory[] {
    return this.documentService.store.exerciseProgram.document.exercise_categories;
  }


  constructor(
    private store: Store,
    private guidedTourDataService: GuidedTourDataService,
    private documentService: DocumentService,
    private backgroundSyncService: BackgroudSyncService,
    private route: ActivatedRoute,
    private authService: AuthService,
    private router: Router,
    private offline: OfflineService,
    private dialog: MatDialog,
    private bus: EventBusService,
    private factory: DocumentModelFactoryService,
    // private printService: DocumentPrintService,
    private playcardDownloadService: PlaycardDownloadService,
    private observerService: ObserverService,
  ) { }

  ngOnInit() {
    this.isLoading = true;

    this.documentService.store.currentPlanDocument = null;
    const initSub = this.documentService
      .init()
      .pipe(
        concatMap(() => {
          if (this.route.snapshot.params.hasOwnProperty('id') && this.route.snapshot.params.id) {
            return this.documentService.setCurrentPlan(this.route.snapshot.params.id);
          } else {
            return of(null);
          }
        }),
        mergeMap(() => {
          return this.authService.currentUserObs.pipe(
            tap(user => {
              this.currentUser = user;
            }),
          );
        }),
      )
      .subscribe(() => {
        this.isLoading = false;
      });

    this.subscriptions.push(initSub);

    const copySub = this.bus.subscribe<CopyDayState>(copyDayTopic, result => {
      this.copying = result.copying;
    });

    this.subscriptions.push(copySub);

    const dayViewSub = this.bus.subscribe<boolean>(dayViewTopic, result => {
      if (result.hasOwnProperty('override')) {
        this.dayView = result['override'];
      } else {
        if (this.dayView && (this.selectedDay.workout.est_completion_time_ms === -1 || this.selectedDay.workout.est_rpe === -1)) {
          this.bus.publish('dayInvalid', false);
        } else {
          this.dayView = result;
        }
      }
    });

    this.subscriptions.push(dayViewSub);

    const refreshSub = this.observerService.observe('Sync finished').subscribe(message => {
      this.isLoading = true;
      const planId = this.plan.uuid;
      this.documentService.store.currentPlanDocument = null;

      this.documentService.setCurrentPlan(planId).subscribe(() => {
        this.isLoading = false;
      });
    });

    this.subscriptions.push(refreshSub);

    const syncStatusSub = this.documentService.syncStatus.subscribe((status: SyncStatus) => {
      this.isSynced = status === SyncStatus.Synced;
    });

    this.subscriptions.push(syncStatusSub);
  }

  ngAfterViewInit() {
    // yeah this is how we're doing it for now
    setTimeout(() => {
      this.guidedTourStatusSub = this.guidedTourToggleStatus$.subscribe(status => {
        if (status !== undefined && this.plan && this.plan.document) {
          this.guidedTourToggledOn = status;
          if (this.guidedTourToggledOn) {
            setTimeout(() => this.guidedTourDataService.startGuidedTour('plan-calendar'), 1000);
          }
        }
      });
      this.store.dispatch(new GuidedTourActions.GetGuidedTourToggleStatus());
    }, 5000);  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    if (this.guidedTourStatusSub) {
      this.guidedTourStatusSub.unsubscribe();
    }
  }

  addWorkout(workout: Workout | Workout[]) {
    const updateDocumentSub = this.updateDocument<Workout>('workouts', workout).subscribe(() => {
      if (this.selectedDay) {
        if (!isArray(workout) && this.selectedDay.workouts.some(w => w.id === workout.id)) {
          this.selectedDay.workout = workout;
        }

        this.selectWorkout(this.selectedDay);
      }
    });
    this.subscriptions.push(updateDocumentSub);
  }

  addStandardWorkout($event: { day: Day; workoutType: WorkoutType }): void {
    this.addWorkout(<Workout>{
      ...this.factory.build<Workout>(DocumentModelType.Workout),
      name: $event.workoutType.name,
      schedule: this.buildSchedule($event.day),
      tiers: $event.workoutType.tiers.sort(Utils.sortTiers).map(tier => {
        return {
          ...this.factory.build<PlannedTier>(DocumentModelType.PlannedTier),
          id: tier.id,
          name: tier.name,
          order: tier.order,
          notes: tier.notes,
        } as PlannedTier;
      }),
      workout_type_id: $event.workoutType.id,
    });
  }

  addCustomWorkout($event: { day: Day }): void {
    this.addWorkout({
      ...this.factory.build<Workout>(DocumentModelType.Workout),
      schedule: this.buildSchedule($event.day),
      tiers: [this.factory.build<PlannedTier>(DocumentModelType.PlannedTier)],
    } as Workout);
  }

  addRestrictedWorkout($event: { day: Day }): void {
    this.dialog
      .open(AddRestrictionDialogComponent, {
        data: this.restrictLabels,
        disableClose: true,
        width: '300px',
      })
      .afterClosed()
      .subscribe((result: any) => {
        if (result) {
          const restrictedWorkout = this.factory.build<Workout>(DocumentModelType.Workout);
          restrictedWorkout.schedule = this.buildSchedule($event.day);

          const foundRestrictLabel = this.restrictLabels.find(restrictLabel => restrictLabel.name === result);
          if (foundRestrictLabel) {
            // if existing restrict label, use properties from exercise program
            restrictedWorkout.name = foundRestrictLabel.name;
            restrictedWorkout.restrict_label_id = foundRestrictLabel.id;
          } else {
            // create a custom restrict label for the day
            restrictedWorkout.name = result;
            restrictedWorkout.restrict_label_id = uuid();
          }

          this.addWorkout(restrictedWorkout);
        }
      });
  }

  addPhase(phases: Phase[]) {
    this.updateDocument<Phase>('phases', phases, true).subscribe();
  }

  /**
   * Maps a day to a schedule property
   * @param day
   */
  private buildSchedule(day: Day): Schedule {
    return {
      day: [day.schedule.day],
      week: [day.schedule.week],
    };
  }

  /**
   * Updates the value of the plan document at the provided path
   * @param path
   * @param value
   * @param replace true to replace value at path, false to push/concatenate
   */
  private updateDocument<T>(path: string, value: T | T[], replace?: boolean): Observable<void> {
    if (this.isOwner) {
      const plan = cloneDeep<PlanDocument>(this.plan);
      const arr = path.split('/');

      if (has(plan.document, arr)) {
        if (replace) {
          update(plan.document, arr, () => {
            return value;
          });
        } else {
          if (isArray(value)) {
            update(plan.document, arr, val => {
              return val.concat(value);
            });
          } else {
            update(plan.document, arr, val => {
              return val.concat([value]);
            });
          }
        }

        return this.documentService.updatePlan(plan).pipe(
          concatMap(() => this.documentService.setCurrentPlan(plan.uuid)),
          tap(() => {
            this.lastUpdated = moment().unix();
            if (this.selectedDay) {
              const workout = this.selectedDay.workout ? this.plan.document.workouts.find(w => w.id === this.selectedDay.workout.id) : null;
              const phase = this.selectedDay.phase ? this.plan.document.phases.find(p => p.id === this.selectedDay.phase.id) : null;

              if (workout) {
                this.selectWorkout({ ...this.selectedDay, workout: workout, phase: phase });
              } else {
                this.selectedDay = undefined;
              }
            }
          }),
        );
      }
    }
  }

  selectWorkout(day: Day) {
    if (day.workout) {
      const selectedDay = <SelectedDay>{
        ...day,
        phase: day.phase ? this.plan.document.phases.find(p => p.id === day.phase.id) : null,
        workout: this.plan.document.workouts.find(w => w.id === day.workout.id),
        workouts: this.plan.document.workouts.filter(
          w => w.schedule.day.some(d => d === day.schedule.day) && w.schedule.week.some(wk => wk === day.schedule.week),
        ),
      };

      if (day.workout.workout_type_id) {
        selectedDay.workoutType = this.workoutTypes.find(type => type.id === (<Workout>day.workout).workout_type_id);
      } else if (day.workout.restrict_label_id) {
        selectedDay.restrictLabel = this.restrictLabels.find(type => type.id === day.workout.restrict_label_id);
      }

      if (
        (this.selectedDay && (!this.selectedDay.date.isSame(selectedDay.date) || this.selectedDay.workout.id !== selectedDay.workout.id)) ||
        !this.selectedDay
      ) {
        this.switchDayView(false);
      }
      this.selectedDay = selectedDay;
    }
  }

  private orderTiers(workout: Workout): PlannedTier[] {
    const tiers = workout.tiers.sort((a, b) => a.order < b.order ? -1 : 1);
    tiers.forEach((tier, index) => {
      tier.order = index + 1;
    });

    return tiers;
  }

  editDay(workout: Workout, updateVersion = false): Observable<void> {
    const workouts = cloneDeep<Workout[]>(this.plan.document.workouts);
    const index = workouts.findIndex(w => w.id === workout.id);
    if (index > -1) {
      const hasChanged = this.documentService.compareWorkouts(workouts[index], workout);

      // ensure tiers are in the right order
      workout.tiers = this.orderTiers(workout);

      workouts[index] = workout;

      if (updateVersion || hasChanged) {
        // update workout version
        if (workouts[index].version >= 0) {
          workouts[index].version++;
        } else {
          workouts[index].version = 0;
        }
      }

      return this.updateDocument<Workout>(
        'workouts',
        workouts.map(w => w),
        true,
      );
    } else {
      return of(null);
    }
  }

  /**
   * Changes the name property of the workout
   * @param data - {id of workout, new name of workout}
   */
  editWorkoutName(data: { workoutId: string; newName: string }) {
    const workoutIndex = this.plan.document.workouts.findIndex(w => w.id === data.workoutId);
    if (workoutIndex > -1) {
      const cloned = cloneDeep(this.plan.document.workouts[workoutIndex]);
      cloned.name = data.newName;
      cloned.complete = false;
      this.editDay(cloned).subscribe();
    }
  }

  /**
   * Updates the workout estimated completion time
   * @param data { id of workout, completion time of workout in minutes}
   */
  editWorkoutCompletionTime(data: { workoutId: string; completionTime: number }) {
    const workoutIndex = this.plan.document.workouts.findIndex(w => w.id === data.workoutId);
    if (workoutIndex > -1) {
      const cloned = cloneDeep(this.plan.document.workouts[workoutIndex]);
      cloned.est_completion_time_ms = data.completionTime * 60 * 1000;
      cloned.complete = false;
      this.editDay(cloned).subscribe();
    }
  }

  /**
   * Updates the workout estimated RPE
   * @param data { id of workout, estimated RPE}
   */
  editWorkoutEstimatedRPE(data: { workoutId: string; estimatedRPE: number }) {
    const workoutIndex = this.plan.document.workouts.findIndex(w => w.id === data.workoutId);
    if (workoutIndex > -1) {
      const cloned = cloneDeep(this.plan.document.workouts[workoutIndex]);
      cloned.est_rpe = data.estimatedRPE;
      cloned.complete = false;
      this.editDay(cloned).subscribe();
    }
  }

  editTier(data: { workoutId: string; index: number; tier: PlannedTier }) {
    const workoutIndex = this.plan.document.workouts.findIndex(w => w.id === data.workoutId);
    if (workoutIndex > -1) {
      const cloned = cloneDeep(this.plan.document.workouts[workoutIndex]);
      let completeObs = of(null);
      if (cloned.complete) {
        cloned.complete = false;
        completeObs = this.editDay(cloned);
      } else {
        completeObs = this.editDay(cloned, true);
      }

      completeObs
        .pipe(concatMap(() => this.updateDocument<PlannedTier>(`workouts/${workoutIndex}/tiers/${data.index}`, data.tier, true)))
        .subscribe();
    }
  }

  editTiers(data: { workoutId: string; tiers: PlannedTier[] }) {
    const workoutIndex = this.plan.document.workouts.findIndex(w => w.id === data.workoutId);
    if (workoutIndex > -1) {
      const cloned = cloneDeep(this.plan.document.workouts[workoutIndex]);
      let completeObs = of(null);
      if (cloned.complete) {
        cloned.complete = false;
        completeObs = this.editDay(cloned);
      } else {
        completeObs = this.editDay(cloned, true);
      }

      completeObs.pipe(concatMap(() => this.updateDocument<PlannedTier[]>(`workouts/${workoutIndex}/tiers`, data.tiers, true))).subscribe();
    }
  }

  switchDayView(override?: boolean) {
    if (this.selectedDay) {
      if (override === undefined && (this.selectedDay.workout.est_completion_time_ms === -1 || this.selectedDay.workout.est_rpe === -1)) {
        this.bus.publish('dayInvalid', true);
      } else {
        this.bus.publish(dayViewTopic, { override: false });
      }
    }
  }

  clearDay(workout: Workout) {
    const workouts = cloneDeep<Workout[]>(this.plan.document.workouts);
    const foundWorkout = this.plan.document.workouts ? this.plan.document.workouts.find(w => w.id === workout.id) : null;

    if (foundWorkout) {
      this.updateDocument<Workout>(
        'workouts',
        workouts.filter(w => w.id !== workout.id),
        true,
      ).subscribe(() => {
        if (this.selectedDay) {
          this.selectWorkout(this.selectedDay);
        }
      });
    }
  }

  /**
   * Given a day, opens dialog component allowing user to choose a workout to add
   * from the user library.
   * @param {Day} day The day the user library workout will be added to
   */
  addWorkoutFromLibrary(day: Day) {
    this.dialog
      .open(AddFromTemplateDialogComponent, {
        width: '400px',
        data: <AddFromTemplateDialogComponentData>{
          title: 'Add Workout From Library',
          items: this.userLibrary && this.userLibrary.workouts ? this.userLibrary.workouts : [],
          label: 'Workout',
        },
      })
      .beforeClosed()
      .pipe(
        concatMap((result: Template) => {
          if (result) {
            // copy the workout
            const workoutCopy = this.factory.copy<Workout>(result.template as Workout);
            // set the workout incomplete by default
            workoutCopy.complete = false;
            // set schedule based on selected day
            workoutCopy.schedule = {
              day: [day.schedule.day],
              week: [day.schedule.week],
            };
            // map each tier to a new tier
            workoutCopy.tiers = (result.template as Workout).tiers.map(tier => {
              const copyTier: PlannedTier = this.factory.copy<PlannedTier>(tier);
              // copy each row to a new row
              copyTier.rows = this.copyPlannedTierRows(tier.rows);
              return copyTier;
            });
            // update document
            this.addWorkout(workoutCopy);
          }
          return of(null);
        }),
      )
      .subscribe();
  }

  /**
   * Opens dialog to choose name for workout template, then adds it to library;
   * @param {Workout} workout - Work that will be added
   */
  addWorkoutToLibrary(workout: Workout) {
    this.dialog
      .open(AddLibraryDialogComponent, {
        width: '400px',
        data: <AddLibraryDialogComponentData>{
          title: 'Add Workout To Library',
          initial: workout.name,
        },
      })
      .beforeClosed()
      .pipe(
        concatMap((result: string) => {
          if (result) {
            return this.documentService.addWorkoutTemplate(result, workout);
          } else {
            return of(null);
          }
        }),
      )
      .subscribe();
  }

  /**
   * Opens a dialog allowing users to choose tier to add from template library.
   * Users also have option to add an empty tier.
   * @param data - object containing ID of workout to add tier to and current list of tiers.
   */
  addTierFromLibrary(data: { workoutId: string; tiers: PlannedTier[] }) {
    this.dialog
      .open(AddFromTemplateDialogComponent, {
        width: '400px',
        data: <AddFromTemplateDialogComponentData>{
          title: 'Add Tier From Library',
          items: this.userLibrary && this.userLibrary.tiers ? this.userLibrary.tiers : [],
          label: 'Tier',
          buttons: [
            {
              label: 'Add Empty',
              callBack: () => null,
            },
          ],
        },
      })
      .beforeClosed()
      .pipe(
        concatMap((result: Template) => {
          // If result === undefined, user canceled in dialog.
          if (result === undefined) {
            return of(null);
          } else {
            // Copy current tiers
            const tiers = cloneDeep<PlannedTier[]>(data.tiers);

            if (result === null) {
              // add empty tier;
              const emptyTier = this.factory.build<PlannedTier>(DocumentModelType.PlannedTier);
              emptyTier.name = `Custom Tier ${data.tiers.length + 1}`;
              emptyTier.order = data.tiers.length + 1;
              tiers.push(emptyTier);
            }
            if (result) {
              // Make copy of tier
              const tierCopy = this.factory.copy<PlannedTier>(result.template as PlannedTier);
              tierCopy.order = data.tiers.length + 1;
              // Make copy of every row in tier
              tierCopy.rows = this.copyPlannedTierRows((result.template as PlannedTier).rows);

              // do a deep copy of tiers, and push the new copied tier
              tiers.push(tierCopy);
            }
            // use editTiers on updated tiers
            this.editTiers({ workoutId: data.workoutId, tiers: tiers });
            return of(null);
          }
        }),
      )
      .subscribe();
  }

  /**
   * Opens dialog to choose name for workout template, then adds it to library;
   * @param {PlannedTier} tier - Tier to add
   */
  addTierToLibrary(tier: PlannedTier) {
    this.dialog
      .open(AddLibraryDialogComponent, {
        width: '400px',
        data: <AddLibraryDialogComponentData>{
          title: 'Add Tier To Library',
          initial: tier.name,
        },
      })
      .beforeClosed()
      .pipe(
        concatMap((result: string) => {
          if (result) {
            return this.documentService.addTierTemplate(result, tier);
          } else {
            return of(null);
          }
        }),
      )
      .subscribe();
  }

  removeLibraryItem(item: Template) {
    this.documentService.removeTemplate(item).subscribe();
  }

  completeDay(workout: Workout, complete: boolean) {
    const cloned = cloneDeep<Workout>(workout);
    cloned.complete = complete !== undefined ? complete : null;
    this.editDay(cloned).subscribe(() => {
      this.bus.publish<{ week: number; day: number; workout: Workout }>(workoutUpdatedTopic, {
        day: workout.schedule.day[0],
        week: workout.schedule.week[0],
        workout: cloned,
      });
    });
  }

  downloadDay(dayId: string) {
    const planUUID = this.plan.uuid;
    this.documentService
      .syncPlan(this.plan.uuid)
      .pipe(
        concatMap(() => this.documentService.restoreData()),
        concatMap(() => this.documentService.setCurrentPlan(planUUID)),
      )
      .subscribe(() => {
        this.playcardDownloadService.download(this.plan.uuid, dayId, this.plan.version);
      });
  }

  edit() {
    this.router.navigate([`document/create-plan/${this.plan.uuid}`]);
  }

  switchView() {
    this.monthView = !this.monthView;
  }

  refresh() {
    this.isLoading = true;
    this.documentService
      .syncPlan(this.plan.uuid)
      .pipe(concatMap(() => this.documentService.setCurrentPlan(this.plan.uuid)))
      .subscribe(() => {
        if (this.selectedDay) {
          this.selectWorkout(this.selectedDay);
        }
        this.isLoading = false;
      });
  }

  share() {
    const plan = this.documentService.store.planMetadata.find(
      p => this.documentService.store.currentPlanDocument && this.documentService.store.currentPlanDocument.uuid === p.uuid,
    );

    if (plan) {
      this.dialog
        .open(UserSearchComponent, {
          width: '400px',
          data: <UserSearchComponentData>{
            selectedUsers: plan.sharedWithUsers && plan.sharedWithUsers.length > 0 ? plan.sharedWithUsers.map(user => new User(user)) : [],
            disableUnshare: true,
            submitButtonText: 'Share',
            disableSelf: true,
            currentUserUUID: this.currentUser.uuid,
            searchByEmail: false,
          },
        })
        .beforeClosed()
        .pipe(
          concatMap((result: UserSearchComponentResult) => {
            if (result) {
              const ids = result.users.map(u => u.uuid);
              return this.documentService.sharePlan(this.plan.uuid, ids);
            } else {
              return of(null);
            }
          }),
        )
        .subscribe();
    }
  }

  publish() {
    this.dialog
      .open(SelectGroupsDialogComponent, {
        width: '400px',
        data: {
          selected: this.publishedGroups,
        },
      })
      .beforeClosed()
      .pipe(
        concatMap((result: string[]) => {
          if (result) {
            return this.documentService.publishPlan(this.plan.uuid, result);
          } else {
            return of(null);
          }
        }),
      )
      .subscribe();
  }

  back($event) {
    $event.preventDefault();
    this.router.navigate(['/planning']);
  }

  /**
   * Adds a workout for each selected day post copy
   */
  addWorkoutsFromSelectedDays($event: { workoutUUID: string; selectedDays: any }): void {
    const workouts: Workout[] = [];

    for (const key in $event.selectedDays) {
      if ($event.selectedDays[key] === true) {
        const date = moment(key);
        const foundWorkout = this.plan.document.workouts.find(w => w.id === $event.workoutUUID);
        if (foundWorkout) {
          const workout = this.factory.copy<Workout>(foundWorkout);
          const schedule = Utils.getSchedule(moment(this.plan.document.start_date), date);
          workout.schedule = <Schedule>{
            day: [schedule.day],
            week: [schedule.week],
          };
          workout.complete = !!workout.complete && workout.est_completion_time_ms > -1 && workout.est_rpe > -1;

          workouts.push(workout);
        }
      }
    }

    if (workouts.length > 0) {
      this.addWorkout(workouts);
    }
  }

  copyPlan() {
    const data: CopyPlanDialogComponentData = {
      plan: this.documentService.store.planMetadata.find(plan => plan.uuid === this.plan.uuid),
      userUUID: this.currentUser.uuid,
    };
    this.dialog.open(CopyPlanDialogComponent, {
      width: '450px',
      data: data,
    });
  }

  printMonth() {
    // const spec: DocumentPrintJob = {
    //   format: 'months',
    //   document: this.plan,
    //   data: null,
    // };
    // this.printService.print(spec);
  }
  printWeek() {
    const ref = this.dialog.open(SelectWeekDialogComponent, {
      data: <SelectWeekDialogComponentData>{
        title: 'Select Week to print:',
        buttonLabel: 'Print',
        startDate: this.plan.document.start_date,
        numWeeks: this.plan.document.num_weeks,
      },
    });
    ref.afterClosed().subscribe((data: SelectWeekDialogComponentResult) => {
      if (data !== undefined) {
        // const spec: DocumentPrintJob = {
        //   format: 'week',
        //   document: this.plan,
        //   data: data,
        // };
        // this.printService.print(spec);
      }
    });
  }

  openProgrammingChart() {
    this.dialog.open(ChartDialogComponent, {
      width: '950px',
    });
  }

  /**
   * Given an array of rows, returns an array of copied rows.
   * Note: can't use factory.copy for rows since PlannedTierRow do not extend Record
   * @param rows rows to copy
   */
  private copyPlannedTierRows(rows: PlannedTierRow[]): PlannedTierRow[] {
    return rows.map(row => {
      const copyRow: PlannedTierRow = this.factory.build<PlannedTierRow>(DocumentModelType.PlannedTierRow);
      copyRow.order = row.order;
      copyRow.exercise_name = row.exercise_name;
      copyRow.exercise_id = row.exercise_id;
      copyRow.notes = row.notes;
      copyRow.parameter_selections = cloneDeep(row.parameter_selections);
      return copyRow;
    });
  }
}
